import { useEffect } from "react";
import { useNavigationType } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import heroVideo from "../assests/videoe.mp4"; // 🎥 Your hero background video

export default function VideoEditing() {
  // Scroll to top when page loads
 

  return (
    <div className="bg-gray-50 text-gray-900 font-orbitron">
      {/* Hero Section with Video Background */}
      <section className="relative w-full h-[70vh] text-white flex items-center justify-center text-center">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={heroVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>

        <div className="relative z-10 px-6 max-w-3xl">
          <motion.h1
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold mb-6 tracking-tight drop-shadow-lg"
          >
            Professional Video Editing Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed text-gray-200"
          >
            Transform raw footage into engaging, high-quality videos for media, corporate, education, and AI datasets.
          </motion.p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-6 text-center max-w-4xl mx-auto">
        <h2 className="text-4xl font-semibold mb-6 text-gray-900">
          Why Video Editing Matters
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed">
          Video is the most powerful storytelling medium, but raw footage alone isn’t enough. Professional editing ensures your videos captivate, inspire, and deliver the intended message for audiences worldwide.
        </p>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-gray-100">
        <h2 className="text-4xl font-semibold text-center mb-12 text-gray-900">
          Our Video Editing Services
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {[
            { title: "🎞 Post-Production Editing", desc: "Smooth cuts, transitions, and storytelling flow." },
            { title: "🎨 Motion Graphics & Animation", desc: "Titles, intros, and visually engaging elements." },
            { title: "🎶 Sound Design & Mixing", desc: "Background music, voice sync, and audio clarity." },
            { title: "🌍 Subtitling & Localization", desc: "Multilingual subtitles and captions for global reach." },
            { title: "📊 Data-driven Editing", desc: "Custom editing for AI datasets and research purposes." },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              className="bg-white p-6 rounded-2xl shadow-md hover:shadow-xl transition"
            >
              <h3 className="text-xl font-semibold mb-3 text-indigo-700">{item.title}</h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-6">
        <h2 className="text-4xl font-semibold text-center mb-12 text-gray-900">
          How We Work
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 max-w-6xl mx-auto text-center">
          {[
            "Analyze raw footage & goals",
            "Editing & sequencing",
            "Add effects, graphics & music",
            "Review & revisions",
            "Final export & delivery",
          ].map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.2 }}
              viewport={{ once: true }}
              className="p-6 bg-white rounded-xl shadow-md hover:shadow-lg"
            >
              <div className="text-4xl mb-3">{`${idx + 1}️⃣`}</div>
              <p className="text-gray-700 leading-relaxed">{step}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-20 px-6 bg-gray-100">
        <h2 className="text-4xl font-semibold text-center mb-12 text-gray-900">
          Applications
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {[
            { title: "📺 Media & Entertainment", desc: "Movies, series, and music videos." },
            { title: "📱 Social Media Content", desc: "Ads, reels, and campaigns." },
            { title: "🏢 Corporate & Training", desc: "Internal communication & e-learning." },
            { title: "🎓 Education", desc: "Online courses, lectures, and tutorials." },
            { title: "🤖 AI & Tech", desc: "Structured video datasets for AI." },
            { title: "🎥 Events", desc: "Weddings, conferences, and live recordings." },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              className="bg-white p-6 rounded-2xl shadow hover:shadow-xl transition"
            >
              <h3 className="font-semibold mb-2 text-lg text-indigo-700">{item.title}</h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 text-center bg-gradient-to-r from-indigo-600 to-purple-700 text-white">
        <h2 className="text-4xl md:text-5xl font-bold mb-6">
          Transform Your Footage Into Impactful Stories
        </h2>
        <p className="text-lg max-w-xl mx-auto mb-6">
          From short clips to full productions, our video editing team ensures your content engages and inspires your audience.
        </p>
        <motion.button
          whileHover={{ scale: 1.1 }}
          className="bg-white text-indigo-700 font-semibold px-6 py-3 rounded-xl shadow-md hover:bg-gray-100 transition"
        >
          🎬 Get Started Today
        </motion.button>
      </section>
    </div>
  );
}
